import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import Text from './Text';
import { Grid } from '../../node_modules/@mui/material/index';
import { useSelector } from 'react-redux';

const graphPoints = (eSpace, fSpace, vSpace) => {
  const eaveSpace = eSpace;
  const fieldSpace = fSpace;
  const verticalSpace = vSpace;

  const rows = verticalSpace ? Math.ceil(114 / verticalSpace) : 0;
  const yCoordinate = 6;
  /**
   * horizontalFieldValue initialize
   * from evenOrigin to avoid parallel
   * snow bracket
   */
  const evenOrigin = yCoordinate + eaveSpace;
  const coordinatesArray = [];
  let countForFisrtRow = 0;
  for (
    let horizontalEaveValue = yCoordinate;
    horizontalEaveValue <= 120;
    horizontalEaveValue += eaveSpace
  ) {
    if (!eaveSpace || countForFisrtRow++ >= 20) break; // to avoid infinite loop
    coordinatesArray.push({
      x: parseFloat(horizontalEaveValue.toFixed(2)),
      y: yCoordinate,
      z: 'eaveRow'
    });
  }

  let horizontalFieldRow = 1;
  let horizontalFieldValue = yCoordinate;
  let yaxis = yCoordinate + verticalSpace;

  while (horizontalFieldRow < rows) {
    if (horizontalFieldRow % 2 == 0) {
      horizontalFieldValue = evenOrigin;
    } else {
      horizontalFieldValue = yCoordinate;
    }
    let count = 0;
    for (horizontalFieldValue; horizontalFieldValue <= 120; horizontalFieldValue += fieldSpace) {
      if (!fieldSpace || count++ >= 10) break; // to avoid infinite loop

      if (horizontalFieldRow == 1) {
        coordinatesArray.push({
          x: parseFloat(horizontalFieldValue.toFixed(2)),
          y: parseFloat(yaxis.toFixed(2)),
          z: 'fieldRow'
        });
      } else {
        coordinatesArray.push({
          x: parseFloat(horizontalFieldValue.toFixed(2)),
          y: parseFloat(yaxis.toFixed(2)),
          z: ''
        });
      }
    }

    yaxis += verticalSpace;
    horizontalFieldRow++;
  }
  return coordinatesArray;
};

let iconUrl = null;

const BracketGraph = ({ eSpace = 0, fSpace = 0, vSpace = 0 }) => {
  const { selectedImages } = useSelector((store) => store.bracketOptions);
  // console.log('eSpace', eSpace, 'vSpace', vSpace, 'fSpace', fSpace);
  const [data, setData] = useState(
    graphPoints(parseFloat(eSpace), parseFloat(fSpace), parseFloat(vSpace))
  );

  const [eaveRow, setEaveRow] = useState([]);
  const [fieldRow, setFieldRow] = useState([]);

  useEffect(() => {
    setData(graphPoints(parseFloat(eSpace), parseFloat(fSpace), parseFloat(vSpace)));

    setEaveRow(data.filter((coordinateZ) => coordinateZ.z.includes('eaveRow')));
    setFieldRow(data.filter((coordinateZ) => coordinateZ.z.includes('fieldRow')));
  }, [eSpace, fSpace, vSpace]);

  useEffect(() => {
    setEaveRow(data.filter((coordinateZ) => coordinateZ.z.includes('eaveRow')));
    setFieldRow(data.filter((coordinateZ) => coordinateZ.z.includes('fieldRow')));
  }, [data]);

  useEffect(() => {
    iconUrl = selectedImages.filter((image) => image?.label === 'Snow Bracket Type')[0]?.iconUrl;
  }, [selectedImages]);

  return (
    <Grid sx={{ width: '100%' }}>
      <Text>
        <span style={{ fontWeight: 'bold' }}>Graphical Representation of 1 Roofing Square</span>
      </Text>
      <div className="bg-gray-200" style={{ borderRadius: 5 }}>
        <ResponsiveContainer width={'95%'} height={400}>
          <ScatterChart
            width={500}
            height={500}
            margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }}>
            <XAxis type="number" dataKey="x" name="horizontal" unit="in" domain={[0, 120]} />
            <YAxis type="number" dataKey="y" name="vertical" unit="in" domain={[0, 120]} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            {data.map((entry, index) => (
              <Scatter
                key={`scatter-${index}`}
                data={[entry]}
                shape={(args) => (
                  <>
                    <image
                      x={args.cx - 10}
                      y={args.cy - 10}
                      xlinkHref={iconUrl}
                      width={40}
                      height={40}
                    />
                  </>
                )}
              />
            ))}

            {/* Eave Space line */}
            {eaveRow?.length > 0 && (
              <Scatter
                data={[
                  {
                    x: eaveRow[0]?.x + 4,
                    y: eaveRow[0]?.y,
                    label: parseFloat(eSpace)?.toFixed(2) + 'in'
                  },
                  { x: parseFloat(eaveRow[1]?.x - 2)?.toFixed(2), y: eaveRow[1]?.y, label: '' }
                ]}
                line={{ stroke: '#000000' }}>
                {/* multiply by 2 for setting the position of label dynamically  */}
                <LabelList dataKey="label" position={{ x: eSpace * 2, y: data[0]?.y - 6 }} />
              </Scatter>
            )}

            {/* Field Space line */}
            {fieldRow?.length > 0 && (
              <Scatter
                data={[
                  {
                    x: fieldRow[0]?.x + 4,
                    y: fieldRow[0]?.y,
                    label: parseFloat(fSpace)?.toFixed(2) + 'in'
                  },
                  { x: parseFloat(fieldRow[1]?.x - 2)?.toFixed(2), y: fieldRow[1]?.y, label: '' }
                ]}
                line={{ stroke: '#000000' }}>
                <LabelList dataKey="label" position={{ x: fSpace * 2.5, y: data[0]?.y - 6 }} />
              </Scatter>
            )}

            {/* Horizontel Space line */}
            {fieldRow?.length > 0 && (
              <Scatter
                data={[
                  {
                    x: 120,
                    y: fieldRow[fieldRow.length - 1]?.y,
                    label: parseFloat(fieldRow[fieldRow.length - 1]?.y - 6)?.toFixed(2) + 'in'
                  },
                  {
                    x: 120,
                    y: 6,
                    label: ''
                  }
                ]}
                line={{ stroke: '#000000' }}>
                <LabelList
                  dataKey="label"
                  position={{ x: 3, y: fieldRow[fieldRow.length - 1]?.y * 1.5 }}
                />
              </Scatter>
            )}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </Grid>
  );
};

BracketGraph.propTypes = {
  eSpace: propTypes.number,
  fSpace: propTypes.number,
  vSpace: propTypes.number,
  totalBrackets: propTypes.number
};

export default BracketGraph;
